/**
 * Super Admin → Platform Invoice Branding
 *
 * Configures the platform's own invoice branding — signature image, footer
 * tagline, T&C and bank details — used by the platform when raising
 * subscription/add-on invoices to tenants. Mirrors the tenant-side
 * "Settings → Invoice branding" card but is **completely independent**:
 * platform books and tenant books never share a single byte of branding.
 */
import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Field, Input, GoldButton, PageHeader } from "@/components/Ui";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPlatformInvoiceBranding() {
    const [form, setForm] = useState({ invoice_footer: "", invoice_terms: "", bank_details: "" });
    const [hasSig, setHasSig] = useState(false);
    const [sigBuster, setSigBuster] = useState(0);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
    const fileRef = useRef(null);

    const load = () => api.get("/admin/invoice-branding").then(r => {
        setForm({
            invoice_footer: r.data.invoice_footer || "",
            invoice_terms: r.data.invoice_terms || "",
            bank_details: r.data.bank_details || "",
        });
        setHasSig(!!r.data.has_signature);
        setMeta({ updated_at: r.data.updated_at, updated_by: r.data.updated_by });
        setSigBuster(Date.now());
    }).catch(e => toast.error(apiError(e)));
    useEffect(() => { load(); }, []);  // eslint-disable-line

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/invoice-branding", form);
            toast.success("Platform invoice branding saved", {
                description: "Will apply to every subscription invoice from now on.",
            });
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const uploadSig = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post("/admin/invoice-branding/signature", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Platform signature uploaded");
            if (fileRef.current) fileRef.current.value = "";
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
    };

    const removeSig = async () => {
        try {
            await api.delete("/admin/invoice-branding/signature");
            toast.success("Platform signature removed");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Platform invoice branding"
                subtitle="Used by every invoice the platform (Law OS) raises to a tenant for their subscription. Tenant invoices to their clients have a completely separate branding configured at Tenant Settings → Invoice branding — books and branding never mix."
                testId="admin-platform-invbrand-header"/>

            <div className="bg-muted border border-border p-6" data-testid="admin-platform-invoice-branding-card">
                <div className="overline mb-2">Platform → Tenant invoices</div>
                <p className="text-xs text-muted-foreground mb-5">
                    Personalises every subscription invoice the platform sends to its tenants. The
                    platform&apos;s company name + GSTIN come from <em>Super Admin → Platform → GST config</em>;
                    the four fields below are the visual layer that ships on top.
                </p>

                <div className="grid lg:grid-cols-2 gap-6">
                    <Field label="Bank / UPI payment details"
                            hint="Shown in a highlighted box. Use line breaks to separate fields. Tenants will see these when paying their subscription invoice.">
                        <textarea value={form.bank_details}
                                    onChange={e => setForm({ ...form, bank_details: e.target.value })}
                                    rows={4} data-testid="platform-invbrand-bank-input"
                                    placeholder={"ICICI Bank · Inno Aim Private Limited\nA/C 1234 5678 9012\nIFSC ICIC0001234\nUPI: innoaim@icici"}
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm font-mono"/>
                    </Field>

                    <Field label="Terms &amp; conditions"
                            hint="Shown in fine print just below the bank details.">
                        <textarea value={form.invoice_terms}
                                    onChange={e => setForm({ ...form, invoice_terms: e.target.value })}
                                    rows={4} data-testid="platform-invbrand-terms-input"
                                    placeholder={"Subscription invoices are due within 7 days.\nLaw OS is operated by Inno Aim Pvt Ltd, Mumbai jurisdiction."}
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>
                    </Field>

                    <Field label="Footer tagline"
                            hint="Single centred line at the very bottom of the PDF.">
                        <Input value={form.invoice_footer} data-testid="platform-invbrand-footer-input"
                                onChange={e => setForm({ ...form, invoice_footer: e.target.value })}
                                placeholder="Thank you for choosing Law OS."/>
                    </Field>

                    <Field label="Authorised signature image"
                            hint="PNG / JPG with transparent background. Max 512 KB. Printed above the footer.">
                        <div className="flex items-start gap-4">
                            <div className="flex-1">
                                <input ref={fileRef} type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                                        onChange={uploadSig} disabled={uploading} data-testid="platform-invbrand-signature-input"
                                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-muted-foreground file:bg-muted file:border-0 file:px-3 file:py-1.5 file:text-foreground file:text-xs file:mr-3 file:cursor-pointer"/>
                                {hasSig && (
                                    <button onClick={removeSig} data-testid="platform-invbrand-signature-remove"
                                        className="mt-2 text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1">
                                        <Trash2 className="w-3 h-3"/> Remove current signature
                                    </button>
                                )}
                            </div>
                            {hasSig && (
                                <div className="bg-white border border-border p-2 max-w-[180px]"
                                        data-testid="platform-invbrand-signature-preview">
                                    <img src={`${BACKEND_URL}/api/admin/invoice-branding/signature/preview?v=${sigBuster}`}
                                            alt="Authorised platform signature"
                                            style={{ maxWidth: 160, maxHeight: 60, objectFit: "contain" }}/>
                                </div>
                            )}
                        </div>
                    </Field>
                </div>

                <div className="mt-6 flex items-center justify-between gap-3">
                    <div className="text-[10px] text-muted-foreground">
                        {meta.updated_at ? <>Last edited {meta.updated_at.slice(0,16).replace("T"," ")} by <code>{meta.updated_by || "—"}</code></> : "Not configured yet."}
                    </div>
                    <GoldButton onClick={save} disabled={saving} data-testid="platform-invbrand-save">
                        {saving ? "Saving…" : "Save platform invoice branding"}
                    </GoldButton>
                </div>
            </div>
        </div>
    );
}
